import { usePropertyGeneralContractorWillWork } from '../../../queries/PropertyFields/NonRentalFlagFields';
import { usePropertyContactIsPrimary } from '../../../queries/PropertyFields/NonRentalContactFields';
import {
    usePropertyRequiredZoningOrEntitlement,
    usePropertyWillBuildOnLandYourself,
} from '../../../queries/PropertyFields/NonRentalLandFields';

import { ShowIf } from '../../components/ShowIf';
import { PropertyAddress } from '../components/PropertyAddress/PropertyAddress';
import { LegalDescription } from '../components/LegalDescription';
import { LandSize } from '../components/LandSize';
import { PlattedLotsCurrent } from './components/PlattedLotsCurrent';
import { PlattedLotsPlanned } from './components/PlattedLotsPlanned';
import { IntendedWork } from '../components/IntendedWork';
import { ZoningOrEntitlement } from './components/ZoningOrEntitlement';
import { RequiredInspections } from './components/RequiredInspections';
import { ExitPlans } from './components/ExitPlans';
import { PerformingSiteWork } from './components/PerformingSiteWork';
import { GeneralContractor } from '../components/GeneralContractor';
import { GeneralContractorCompany } from '../components/GeneralContractorCompany';
import { GeneralContractorFile } from '../../components/FileOrFormQuestions/questions/GeneralContractorFile';
import { OnsitePropertyContact } from '../components/OnsitePropertyContact';
import { OnsitePropertyContactInformation } from '../components/OnsitePropertyContactInformation/OnsitePropertyContactInformation';
import { BudgetFileLand } from '../../components/FileOrFormQuestions/questions/BudgetFileLand';
import { SurveyFile } from '../../components/FileOrFormQuestions/questions/SurveyFile';
import { BuildOnLandYourself } from './components/BuildOnLandYourself';
import { ProvideLaterAdvisory } from '../components/ProvideLaterAdvisory';

export interface LandProps {
    disableEditFormOptionBudget: boolean;
    propertyGuid: string;
    isReview?: boolean;
}

export const Land: React.FC<LandProps> = ({ disableEditFormOptionBudget, propertyGuid, isReview = false }) => {
    const [generalContractorWillWork] = usePropertyGeneralContractorWillWork(propertyGuid);
    const [contactIsPrimary] = usePropertyContactIsPrimary(propertyGuid);
    const [requiredZoningOrEntitlement] = usePropertyRequiredZoningOrEntitlement(propertyGuid);
    const [willBuildOnLandYourself] = usePropertyWillBuildOnLandYourself(propertyGuid);

    return (
        <>
            <PropertyAddress
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <ShowIf showIf={!isReview}>
                <br />
                <br />
                <div className='pageSubHeader'>Additional Information</div>
            </ShowIf>
            <LegalDescription
                propertyGuid={propertyGuid}
                isReview={isReview}
            />
            <LandSize
                propertyGuid={propertyGuid}
                isReview={isReview}
            />
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                <PlattedLotsCurrent
                    propertyGuid={propertyGuid}
                    required={true}
                    isReview={isReview}
                />
                <PlattedLotsPlanned
                    propertyGuid={propertyGuid}
                    required={true}
                    isReview={isReview}
                />
            </div>
            <IntendedWork
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <ZoningOrEntitlement
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <ShowIf showIf={requiredZoningOrEntitlement === true}>
                <RequiredInspections
                    propertyGuid={propertyGuid}
                    required={true}
                    isReview={isReview}
                />
            </ShowIf>
            <GeneralContractor
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <ShowIf showIf={generalContractorWillWork}>
                <br />
                <br />
                <div className='pageSubHeader'>General Contractor</div>
                <GeneralContractorCompany
                    propertyGuid={propertyGuid}
                    required={true}
                    isReview={isReview}
                />
                <GeneralContractorFile
                    propertyGuid={propertyGuid}
                    required={true}
                    isReview={isReview}
                />
            </ShowIf>
            <ShowIf showIf={!generalContractorWillWork}>
                <OnsitePropertyContact
                    propertyGuid={propertyGuid}
                    required={true}
                    isReview={isReview}
                />
            </ShowIf>
            <ShowIf showIf={!generalContractorWillWork && contactIsPrimary === false}>
                <OnsitePropertyContactInformation
                    propertyGuid={propertyGuid}
                    isReview={isReview}
                    required={true}
                />
            </ShowIf>
            <ShowIf showIf={!isReview}>
                <br />
                <br />
                <div className='pageSubHeader'>Additional Files</div>
            </ShowIf>
            <BudgetFileLand
                propertyGuid={propertyGuid}
                disableEditFormOptionBudget={disableEditFormOptionBudget}
                required={true}
                isReview={isReview}
            />
            <SurveyFile
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <BuildOnLandYourself
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <ShowIf showIf={willBuildOnLandYourself === false}>
                <ExitPlans
                    propertyGuid={propertyGuid}
                    required={true}
                    isReview={isReview}
                />
            </ShowIf>
            <PerformingSiteWork
                propertyGuid={propertyGuid}
                required={true}
                isReview={isReview}
            />
            <ProvideLaterAdvisory
                propertyGuid={propertyGuid}
                isReview={isReview}
            />
        </>
    );
};
